import * as React from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Spinner } from '@fluentui/react-components';
import { ChevronRightRegular, FolderRegular } from '@fluentui/react-icons';
import { errorText, useApp } from '../app/context';
import { type IItem, listChildren } from '../graphx';
import { moveProblem } from '../structure/rename';

const SET_ASIDE: RegExp = /^(set aside by ai admin|set aside|_quarantine)$/i;

interface ICrumb {
  id: string;
  name: string;
}

/**
 * Pick where a folder goes: browse folders only, then "Move here". The folder itself, anything
 * inside it and AI Admin's own folders can't be picked.
 */
export default function MoveFolderDialog(props: {
  folder: { name: string; path: string };
  root: ICrumb;
  working: boolean;
  onMove: (parentPath: string, label: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const [trail, setTrail] = React.useState<ICrumb[]>([props.root]);
  const [folders, setFolders] = React.useState<IItem[] | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const here: ICrumb = trail[trail.length - 1];
  const herePath: string = trail.slice(1).map(c => c.name).join('/');
  const problem: string | undefined = moveProblem(props.folder.path, herePath);

  React.useEffect(() => {
    let cancelled: boolean = false;
    setFolders(undefined);
    listChildren(app.graph, w.driveId, here.id, 2000)
      .then(list => !cancelled && setFolders(list.filter(i => i.isFolder).sort((a, b) => a.name.localeCompare(b.name))))
      .catch(e => !cancelled && setError(errorText(e)));
    return () => { cancelled = true; };
  }, [app.graph, w.driveId, here.id]);

  const blocked = (item: IItem): boolean => {
    const path: string = herePath ? `${herePath}/${item.name}` : item.name;
    const self: string = props.folder.path.toLowerCase();
    return path.toLowerCase() === self || path.toLowerCase().indexOf(`${self}/`) === 0
      || (trail.length === 1 && (SET_ASIDE.test(item.name) || item.name.toLowerCase() === w.inboxName.toLowerCase()));
  };

  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open && !props.working) { props.onCancel(); } }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Move "{props.folder.name}"</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="small">Open the folder it should go in, then choose Move here. Everything inside moves with it, and you can undo it from History.</span>
            <nav aria-label="Destination path" className="crumbs">
              {trail.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && <ChevronRightRegular fontSize={12} aria-hidden="true" />}
                  {i < trail.length - 1
                    ? <button type="button" className="link-btn" onClick={() => setTrail(trail.slice(0, i + 1))}>{c.name}</button>
                    : <span aria-current="location" style={{ fontWeight: 600 }}>{c.name}</span>}
                </React.Fragment>
              ))}
            </nav>
            <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
              {error && <p className="small" style={{ padding: '10px 14px', color: 'var(--red)' }}>{error}</p>}
              {!folders && !error && <div style={{ padding: 14 }}><Spinner size="tiny" label="Opening…" labelPosition="after" /></div>}
              {folders?.length === 0 && <p className="small" style={{ padding: '10px 14px', margin: 0 }}>No folders in here.</p>}
              {folders && folders.length > 0 && (
                <ul className="explorer" style={{ maxHeight: 300 }}>
                  {folders.map(f => (
                    <li key={f.id}>
                      <button type="button" className="explorer-row" disabled={blocked(f)} style={blocked(f) ? { opacity: 0.45, cursor: 'default' } : undefined}
                        onClick={() => setTrail([...trail, { id: f.id, name: f.name }])}>
                        <FolderRegular fontSize={18} color="var(--blue)" />
                        <span className="explorer-name">{f.name}</span>
                        {!blocked(f) && <ChevronRightRegular fontSize={16} aria-hidden="true" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {problem && <span className="small">{problem}</span>}
          </DialogContent>
          <DialogActions>
            <Button disabled={props.working} onClick={props.onCancel}>Cancel</Button>
            <Button appearance="primary" disabled={props.working || !!problem} onClick={() => props.onMove(herePath, here.name)}>
              {props.working ? 'Moving…' : `Move here${trail.length > 1 ? ` (into ${here.name})` : ' (top of the library)'}`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
